/*!
 * bang.js — bang(): roll a sound's line again, Pd style
 * © 2026 Rob Canning — GPLv3
 *
 *     bang(s)                    the line s was last run with, its rand()s
 *                                rolled afresh for every voice sounding -- a
 *                                chord built up becomes a new chord of as
 *                                many notes, through the envelope (each
 *                                releases on its r:, the new ones come in on
 *                                their a:), as Ctrl+Alt+Enter
 *     bang(s, 4)                 a metro: now, then every 4 beats
 *     bang(s, Pwhite(1, 5, inf)) ... the time to the next drawn each bang
 *     bang(s, rand(2, 8))        ... likewise (every:... says the same)
 *     bang(s, 0)                 the metro stops (hush(s) stops it too)
 *     bang([a, b]), bang(all)    several, every one
 *
 * A metro run again replaces the one going. A pattern that runs out stops
 * it. A sound not sounding starts again as its line would. Synths
 * (synth.js keeps each sound's line as written, and the metros).
 */

import { synthLine, setBangTimer } from "./synth.js";
import { levelArgs } from "./audio/level.js";
import { synthNames } from "./audio/voices.js";
import { makeValueGenerator, evalMaybeRandom } from "./audio/audioShared.js";
import { gridNow, beatsToSec } from "./audio/beatGrid.js";
import { handleCueTrigger } from "./cueDispatcher.js";

/** One bang: the sound's line again, every voice rolling its own. */
function bangOnce(name) {
  const line = synthLine(name);
  if (!line) { console.warn(`[bang] ${name}: no line run for it yet`); return false; }
  handleCueTrigger(line, false, true, null, { live: true, allVoices: true, _start: true });   // a trig:wait line too
  return true;
}

/** A metro on `name`: a bang now, then each `every` beats (a number, a pattern, a rand()). */
function startMetro(name, every) {
  const gen = every && typeof every === "object" && every.type === "pattern" ? makeValueGenerator(every) : null;
  const next = () => Number(evalMaybeRandom(gen ? gen.next() : every));
  const tick = () => {
    if (!bangOnce(name)) { setBangTimer(name, null); return; }
    const beats = next();
    if (!(beats > 0)) { setBangTimer(name, null); return; }   // run out (or 0): it stops
    setBangTimer(name, setTimeout(tick, beatsToSec(gridNow().ms, beats) * 1000));
  };
  setBangTimer(name, null);
  tick();
}

/** bang(names, every) as a cue. */
export function handleBangCue(ast) {
  const a = levelArgs(ast?.args);
  if (!a.names.length) { console.warn("[bang] which sound? bang(s) / bang(s, 4)"); return; }
  const every = ast.args.find(x => x.type === "every" || x.type === "value")?.value ?? a.numbers[0];
  for (const n of a.names.includes("all") ? synthNames() : a.names) {
    if (every == null) bangOnce(n);
    else if (typeof every !== "object" && !(Number(every) > 0)) setBangTimer(n, null);   // bang(s, 0)
    else startMetro(n, every);
  }
}
